/**
 * TeamsAndAgentsSection — the Usage report's "Teams and Agents" section: the
 * Agents' sell-side spend pivoted Team x Agent, by Team, or by Agent, each
 * figure beside what the same tokens cost at Claude list (matched tier).
 *
 * Every Team and Agent opens its drill (/usage/teams/:deskId,
 * /usage/agents/:agentId) on the same range. ADR-0028: sell side only.
 *
 * Past ten Teams (most of them personal, one per developer) the Team pivots
 * gain a toolbar to find, filter and sort Teams, show the top ten with the
 * rest summed in one rollup row, and list the Teams with no spend apart.
 * The Agent total footer always covers the whole report (teamRows.ts).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import type {
  YagniCodeFrontierComparison,
  YagniCodeIdleTeam,
  YagniCodeTeamAgentUsageReport,
  YagniCodeTeamUsageTotal,
  YagniCodeWorkerUsageReport,
} from "@yagni/shared";

import { Button } from "../ui/button.js";
import { Sec, Seg } from "./usageParts.js";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible.js";
import { NativeSelect } from "../ui/native-select.js";
import {
  agentDrillPath,
  agentLabel,
  basisSavingsPercent,
  fetchTeamAgentUsage,
  formatUsd,
  orderAgents,
  teamAccessibleLabel,
  teamDrillPath,
  teamLabel,
  teamSublabel,
  type UsageRange,
} from "./usageReport.js";
import {
  TEAM_ROW_LIMIT,
  rollupLabel,
  shapeTeamRows,
  sumHidden,
  type TeamKind,
  type TeamRowsView,
  type TeamSort,
} from "./teamRows.js";
import { ClaudeCells, SAVE_COLOR } from "./usageGlass.js";

export type FetchTeamAgentUsage = (range: UsageRange) => Promise<YagniCodeTeamAgentUsageReport>;

type Pivot = "matrix" | "team" | "agent";

const PIVOTS: Array<{ key: Pivot; label: string }> = [
  { key: "matrix", label: "Team × Agent" },
  { key: "team", label: "By Team" },
  { key: "agent", label: "By Agent" },
];

const KINDS: Array<{ key: TeamKind; label: string }> = [
  { key: "all", label: "All" },
  { key: "shared", label: "Shared" },
  { key: "personal", label: "Personal" },
];

const SORTS: Array<{ key: TeamSort; label: string }> = [
  { key: "billed", label: "Most billed" },
  { key: "saved", label: "Most saved" },
  { key: "name", label: "Name" },
];

const SEARCH_LABEL = "Find a Team or owner";

const mono = "font-[family-name:var(--font-mono)]";
const TH = "px-4 py-3 font-semibold text-[var(--text-secondary)]";
const LINK = "text-[var(--accent)] no-underline hover:underline underline-offset-2";
/** The Team column stays put while a narrow screen scrolls the Agents
 * sideways; wide screens keep the plain glass table. */
const STICKY = "max-md:sticky max-md:left-0 max-md:z-[1] max-md:bg-[var(--print-bg-solid)]";

/** "41% saved" under a billed figure; nothing when there is no saving to
 * claim (see basisSavingsPercent). */
function SavedShare({ frontier }: { frontier: YagniCodeFrontierComparison | undefined }) {
  const percent = frontier ? basisSavingsPercent(frontier.matched) : null;
  if (percent === null) return null;
  return (
    <span className="sub" style={{ color: SAVE_COLOR }}>
      {percent}% saved
    </span>
  );
}

/** A matrix cell's accessible name: its Agent and Team, billed, and the
 * saved share it shows, so a screen reader hears what the cell reads. */
function cellAriaLabel(
  agent: string,
  team: string,
  sellMillicents: number,
  frontier: YagniCodeFrontierComparison | undefined,
): string {
  const percent = frontier ? basisSavingsPercent(frontier.matched) : null;
  const saved = percent === null ? "" : `, ${percent}% saved`;
  return `${agent} on ${team}: ${formatUsd(sellMillicents)}${saved}`;
}

export function TeamsAndAgentsSection({
  range,
  fetchTeamUsage = fetchTeamAgentUsage,
  workerUsage,
  workerUsageError,
}: {
  range: UsageRange;
  fetchTeamUsage?: FetchTeamAgentUsage;
  /** The `/workers` report the parent already loads: the By Agent pivot's
   * rows and their standing-job lines. */
  workerUsage: YagniCodeWorkerUsageReport | null;
  workerUsageError: string | null;
}) {
  const [pivot, setPivot] = useState<Pivot>("matrix");
  const [report, setReport] = useState<YagniCodeTeamAgentUsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Held here, not in the pivots, so they survive switching between the
  // Team x Agent and By Team pivots. Not in the URL.
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<TeamKind>("all");
  const [sort, setSort] = useState<TeamSort>("billed");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReport(null);
    setError(null);
    fetchTeamUsage(range)
      .then((loaded) => {
        if (!cancelled) setReport(loaded);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load Team usage");
      });
    return () => {
      cancelled = true;
    };
  }, [fetchTeamUsage, range]);

  const shaped = report === null ? null : shapeTeamPivot(report, { query, kind, sort, showAll });
  const view = shaped?.view ?? null;
  const filters: TeamFilters = {
    query,
    onQuery: setQuery,
    onKind: setKind,
    sort,
    onSort: setSort,
    showAll,
    onShowAll: setShowAll,
    onClear: () => {
      setQuery("");
      setKind("all");
    },
  };

  const kindSeg =
    pivot !== "agent" && report !== null && hasBothKinds(report) ? (
      <Seg label="Team kind" options={KINDS} value={kind} onChange={setKind} />
    ) : null;

  return (
    <Sec
      k="02"
      title="Teams and Agents"
      fact="your Agents, at the same rates"
      testId="teams-and-agents"
      right={
        <>
          <Seg label="Pivot" options={PIVOTS} value={pivot} onChange={setPivot} />
          {kindSeg}
        </>
      }
    >
      {pivot === "agent" ? (
        <ByAgent range={range} workerUsage={workerUsage} error={workerUsageError} />
      ) : error !== null ? (
        <p className="text-xs text-[var(--text-tertiary)]">
          Team spend could not be loaded. Figures above are unaffected.
        </p>
      ) : report === null || shaped === null ? (
        <p className="text-xs text-[var(--text-tertiary)]">Loading Team spend</p>
      ) : (
        <TeamPivot pivot={pivot} range={range} report={report} shaped={shaped} filters={filters} />
      )}

      <p className="text-xs text-[var(--text-secondary)]">
        Each Agent bills at the same lane rates as your developers. At Claude list is the same
        tokens at the Claude model matched to the lane each dispatch ran on, Claude prompt writing
        included. Open an Agent for its all Sonnet and all Opus comparisons and its largest runs.
      </p>
      {pivot !== "agent" && report !== null && report.cells.length > 0 && view !== null && (
        <RollupNote view={view} />
      )}
      {report !== null && report.teams.some((team) => team.deskId === null) && (
        <p className="text-xs text-[var(--text-tertiary)]">
          No Team holds Agent work with no Team behind it, such as checking a repository's
          environment, and Bailey's work from before each call recorded its Team. Every column still
          adds up to the Agent's total.
        </p>
      )}
      {report?.frontier.counterfactualIncomplete && (
        <p className="text-xs text-[var(--text-tertiary)]">
          Some Agent spend ran on a lane with no Claude list equivalent, so the Claude figures cover
          part of the bill rather than guess.
        </p>
      )}
    </Sec>
  );
}

/** The many-Teams controls' state, held by the section so it survives
 * switching pivots, and its setters. */
interface TeamFilters {
  query: string;
  onQuery: (query: string) => void;
  onKind: (kind: TeamKind) => void;
  sort: TeamSort;
  onSort: (sort: TeamSort) => void;
  showAll: boolean;
  onShowAll: (update: (open: boolean) => boolean) => void;
  onClear: () => void;
}

/** What the Team pivots render from: the shaped rows plus which controls
 * apply. Small workspaces get no toolbar, so no filter reaches them. */
interface ShapedTeamPivot {
  view: TeamRowsView;
  withToolbar: boolean;
  withKinds: boolean;
  activeKind: TeamKind;
  activeQuery: string;
  filtering: boolean;
  teamCount: number;
}

function shapeTeamPivot(
  report: YagniCodeTeamAgentUsageReport,
  opts: { query: string; kind: TeamKind; sort: TeamSort; showAll: boolean },
): ShapedTeamPivot {
  const idleCount = report.idleTeams?.length ?? 0;
  const namedCount = report.teams.filter((team) => team.deskId !== null).length;
  const withToolbar = namedCount + idleCount > TEAM_ROW_LIMIT;
  const withKinds = hasBothKinds(report);
  const activeKind = withKinds ? opts.kind : "all";
  const activeQuery = withToolbar ? opts.query : "";
  const view = shapeTeamRows(report, {
    query: activeQuery,
    kind: activeKind,
    sort: opts.sort,
    showAll: opts.showAll,
  });
  return {
    view,
    withToolbar,
    withKinds,
    activeKind,
    activeQuery,
    filtering: activeQuery.trim() !== "" || activeKind !== "all",
    teamCount: view.namedCount + idleCount,
  };
}

/** The Team x Agent and By Team pivots with their many-Teams controls:
 * toolbar, table, Show all, the no-match line and the idle Teams. */
function TeamPivot({
  pivot,
  range,
  report,
  shaped,
  filters,
}: {
  pivot: Exclude<Pivot, "agent">;
  range: UsageRange;
  report: YagniCodeTeamAgentUsageReport;
  shaped: ShapedTeamPivot;
  filters: TeamFilters;
}) {
  const { view, filtering, activeQuery } = shaped;
  const hasSpend = report.cells.length > 0;
  const noMatch = activeQuery.trim() !== "" && view.matchedCount === 0;
  return (
    <>
      {shaped.withToolbar && (
        <TeamsToolbar
          query={filters.query}
          onQuery={filters.onQuery}
          sort={filters.sort}
          onSort={filters.onSort}
          teamCount={shaped.teamCount}
          personalCount={view.personalCount}
          caption={
            filtering
              ? `Showing ${view.matchedCount} of ${shaped.teamCount} Teams. Agent totals cover every Team.`
              : null
          }
        />
      )}
      {!hasSpend ? (
        <div className="empty dashed tight">
          <p>No Agent spend in this range.</p>
        </div>
      ) : pivot === "matrix" ? (
        <Matrix range={range} report={report} view={view} />
      ) : (
        <ByTeam range={range} report={report} view={view} />
      )}
      {hasSpend && !filtering && canShowAll(view, filters.showAll) && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => filters.onShowAll((open) => !open)}
          className="px-0 text-[var(--accent)] hover:bg-transparent hover:text-[var(--accent-hover)]"
        >
          {filters.showAll ? `Show the top ${TEAM_ROW_LIMIT}` : `Show all ${view.namedCount} Teams`}
        </Button>
      )}
      {noMatch && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="m-0 text-xs text-[var(--text-tertiary)]">
            No Team or owner matches "{activeQuery.trim()}".
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={filters.onClear}
            className="px-0 text-[var(--accent)] hover:bg-transparent hover:text-[var(--accent-hover)]"
          >
            Clear
          </Button>
        </div>
      )}
      <IdleTeams idle={view.idle} filtering={filtering} />
    </>
  );
}

/** The Show all toggle shows while Teams are capped, and stays to fold
 * them back once expanded. */
function canShowAll(view: TeamRowsView, showAll: boolean): boolean {
  return view.hiddenReason === "cap" || (showAll && view.namedCount > TEAM_ROW_LIMIT);
}

/** The kind pills only mean something when there are Teams of both kinds. */
function hasBothKinds(report: YagniCodeTeamAgentUsageReport): boolean {
  const teams = [...report.teams.filter((team) => team.deskId !== null), ...(report.idleTeams ?? [])];
  return (
    teams.some((team) => team.personalOwner != null) &&
    teams.some((team) => team.personalOwner == null)
  );
}

function TeamsToolbar({
  query,
  onQuery,
  sort,
  onSort,
  teamCount,
  personalCount,
  caption,
}: {
  query: string;
  onQuery: (query: string) => void;
  sort: TeamSort;
  onSort: (sort: TeamSort) => void;
  teamCount: number;
  personalCount: number;
  caption: string | null;
}) {
  return (
    <div className="space-y-1.5" data-testid="teams-toolbar">
      <div className="flex flex-wrap items-center gap-2">
        <label
          className="flex min-w-[12rem] flex-1 items-center gap-1.5 rounded-md border px-2 focus-within:ring-2 focus-within:ring-border-focus"
          style={{ borderColor: "var(--border)", background: "var(--bg-input)" }}
        >
          <Search size={12} aria-hidden style={{ color: "var(--text-tertiary)" }} />
          <span className="sr-only">{SEARCH_LABEL}</span>
          <input
            type="search"
            value={query}
            onChange={(event) => onQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape" && query.length > 0) {
                event.preventDefault();
                onQuery("");
              }
            }}
            placeholder={SEARCH_LABEL}
            className="min-w-0 flex-1 border-0 bg-transparent py-1.5 text-[12.5px] focus:outline-none"
            style={{ color: "var(--text-primary)" }}
          />
        </label>
        <NativeSelect
          aria-label="Sort Teams"
          selectSize="sm"
          wrapperClassName="w-[9.5rem]"
          value={sort}
          onChange={(event) => {
            const next = SORTS.find((option) => option.key === event.target.value);
            if (next) onSort(next.key);
          }}
        >
          {SORTS.map(({ key, label }) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </NativeSelect>
        <span className={`ml-auto text-[10.5px] text-[var(--text-tertiary)] ${mono}`}>
          {personalCount > 0
            ? `${teamCount} Teams · ${personalCount} personal`
            : `${teamCount} Teams`}
        </span>
      </div>
      <p aria-live="polite" className="m-0 text-xs text-[var(--text-tertiary)]">
        {caption}
      </p>
    </div>
  );
}

/** A Team's row header: its name opening its drill, and under it the
 * owner of a personal Team, or that it is archived. A Deleted Team still
 * opens its drill. */
function TeamRowHeader({ team, range }: { team: YagniCodeTeamUsageTotal; range: UsageRange }) {
  const sublabel = teamSublabel(team);
  return (
    <th scope="row" className={`px-4 py-3 text-left font-medium ${STICKY}`}>
      <Link to={teamDrillPath(team.deskId, range)} className={LINK}>
        {teamLabel(team)}
      </Link>
      {sublabel !== null && (
        <span className="block text-[11px] font-normal text-[var(--text-tertiary)]">{sublabel}</span>
      )}
    </th>
  );
}

/** What the rollup row holds and how to see its Teams one by one. Under a
 * filter the Show all toggle is gone, so the note points at the filter. With
 * only No Team folded in there is no Team to explain. */
function RollupNote({ view }: { view: TeamRowsView }) {
  if (view.hiddenNamedCount === 0) return null;
  return (
    <p className="text-xs text-[var(--text-tertiary)]">
      {view.hiddenReason === "filter"
        ? "Teams not shown are summed in one row, billed only. Clear the filter, or open a Team, for its Claude comparison."
        : "Teams not listed are summed in one row, billed only. Show all, or open a Team, for its Claude comparison."}
    </p>
  );
}

function RollupHeader({ label }: { label: string }) {
  return (
    <th scope="row" className={`px-4 py-3 text-left font-medium text-[var(--text-secondary)] ${STICKY}`}>
      {label}
    </th>
  );
}

/** The Teams with no Agent spend in the range: apart from the table and in
 * no total, closed until asked for. Under a search or kind filter they are
 * matches the caption counts, so they show open, named as matching; the
 * reader's own open or closed choice comes back when the filter clears. */
function IdleTeams({ idle, filtering }: { idle: YagniCodeIdleTeam[]; filtering: boolean }) {
  const [open, setOpen] = useState(false);
  if (idle.length === 0) return null;
  if (filtering) {
    return (
      <div data-testid="idle-teams">
        <p className="m-0 text-xs text-[var(--text-secondary)]">
          {idle.length === 1
            ? "1 matching Team had no Agent spend in this range"
            : `${idle.length} matching Teams had no Agent spend in this range`}
        </p>
        <IdleTeamList idle={idle} />
      </div>
    );
  }
  return (
    <Collapsible data-testid="idle-teams" open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="group flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
        <ChevronRight size={12} aria-hidden className="group-data-[state=open]:rotate-90" />
        {idle.length === 1
          ? "1 Team had no Agent spend in this range"
          : `${idle.length} Teams had no Agent spend in this range`}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <IdleTeamList idle={idle} />
      </CollapsibleContent>
    </Collapsible>
  );
}

function IdleTeamList({ idle }: { idle: YagniCodeIdleTeam[] }) {
  return (
    <ul className="m-0 mt-2 flex list-none flex-wrap gap-x-5 gap-y-2 p-0">
      {idle.map((team) => {
        const sublabel = teamSublabel(team);
        return (
          <li key={team.deskId} className="text-xs">
            <Link to={`/desks/${encodeURIComponent(team.deskId)}`} className={LINK}>
              {team.deskName}
            </Link>
            {sublabel !== null && (
              <span className="ml-1.5 text-[11px] text-[var(--text-tertiary)]">{sublabel}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Matrix({
  range,
  report,
  view,
}: {
  range: UsageRange;
  report: YagniCodeTeamAgentUsageReport;
  view: TeamRowsView;
}) {
  const agents = orderAgents(report.agents);
  const byCell = new Map(report.cells.map((cell) => [`${cell.deskId ?? ""} ${cell.agentId}`, cell]));
  const rollup = view.hidden.length > 0 ? sumHidden(report.cells, view.hidden) : null;
  const label = rollupLabel(view);
  const teamRow = (team: YagniCodeTeamUsageTotal) => (
    <tr key={team.deskId ?? "none"} className="border-b border-[var(--glass-border)]">
      <TeamRowHeader team={team} range={range} />
      {agents.map((agent) => {
        const cell = byCell.get(`${team.deskId ?? ""} ${agent.agentId}`);
        return (
          <td key={agent.agentId} className={`px-4 py-3 text-right ${mono}`}>
            {cell ? (
              <Link
                to={agentDrillPath(agent.agentId, range)}
                className="text-[var(--text-primary)] no-underline hover:underline"
                aria-label={cellAriaLabel(
                  agentLabel(agent),
                  teamAccessibleLabel(team),
                  cell.sellMillicents,
                  cell.frontier,
                )}
              >
                {formatUsd(cell.sellMillicents)}
                <SavedShare frontier={cell.frontier} />
              </Link>
            ) : (
              <span className="text-[var(--text-tertiary)]">-</span>
            )}
          </td>
        );
      })}
      <td className={`px-4 py-3 text-right ${mono} font-semibold`}>
        {formatUsd(team.sellMillicents)}
        <SavedShare frontier={team.frontier} />
      </td>
    </tr>
  );
  return (
    <div className="tbl usage-tbl" data-testid="team-agent-matrix">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[var(--glass-border)] text-left">
            <th scope="col" className={`${TH} ${STICKY}`}>
              Team
            </th>
            {agents.map((agent) => (
              <th key={agent.agentId} scope="col" className={`${TH} num`}>
                <Link to={agentDrillPath(agent.agentId, range)} className={LINK}>
                  {agentLabel(agent)}
                </Link>
              </th>
            ))}
            <th scope="col" className={`${TH} num`}>
              Team total
            </th>
          </tr>
        </thead>
        <tbody>
          {view.rows.map(teamRow)}
          {rollup && (
            <tr className="border-b border-[var(--glass-border)]" data-testid="team-rollup">
              <RollupHeader label={label} />
              {agents.map((agent) => {
                const sell = rollup.byAgent.get(agent.agentId);
                return sell === undefined ? (
                  <td key={agent.agentId} className={`px-4 py-3 text-right ${mono}`}>
                    <span className="text-[var(--text-tertiary)]">-</span>
                  </td>
                ) : (
                  <td
                    key={agent.agentId}
                    className={`px-4 py-3 text-right ${mono} text-[var(--text-secondary)]`}
                    aria-label={`${agentLabel(agent)} on ${label}: ${formatUsd(sell)}`}
                  >
                    {formatUsd(sell)}
                  </td>
                );
              })}
              <td className={`px-4 py-3 text-right ${mono} font-semibold text-[var(--text-secondary)]`}>
                {formatUsd(rollup.sellMillicents)}
              </td>
            </tr>
          )}
          {view.noTeam && teamRow(view.noTeam)}
        </tbody>
        <tfoot>
          <tr className="border-t border-[var(--ink)]">
            <th scope="row" className={`px-4 py-3 text-left font-semibold ${STICKY}`}>
              Agent total
            </th>
            {agents.map((agent) => (
              <td key={agent.agentId} className={`px-4 py-3 text-right ${mono} font-semibold`}>
                {formatUsd(agent.sellMillicents)}
                <SavedShare frontier={agent.frontier} />
              </td>
            ))}
            <td className={`px-4 py-3 text-right ${mono} font-semibold`}>
              {formatUsd(report.totalSellMillicents)}
              <SavedShare frontier={report.frontier} />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ByTeam({
  range,
  report,
  view,
}: {
  range: UsageRange;
  report: YagniCodeTeamAgentUsageReport;
  view: TeamRowsView;
}) {
  const rollup = view.hidden.length > 0 ? sumHidden(report.cells, view.hidden) : null;
  const teamRow = (team: YagniCodeTeamUsageTotal) => (
    <tr key={team.deskId ?? "none"} className="border-b border-[var(--glass-border)]">
      <TeamRowHeader team={team} range={range} />
      <td className={`px-4 py-3 text-right ${mono} text-[var(--text-secondary)]`}>
        {team.runs.toLocaleString("en-US")}
      </td>
      <td className={`px-4 py-3 text-right ${mono} font-semibold`}>
        {formatUsd(team.sellMillicents)}
      </td>
      <ClaudeCells frontier={team.frontier} />
    </tr>
  );
  return (
    <div className="tbl usage-tbl" data-testid="team-usage">
      <table className="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[var(--glass-border)] text-left">
            <th scope="col" className={`${TH} ${STICKY}`}>
              Team
            </th>
            <th scope="col" className={`${TH} num`}>
              Runs
            </th>
            <th scope="col" className={`${TH} num`}>
              Billed
            </th>
            <th scope="col" className={`${TH} num`}>
              At Claude list
            </th>
            <th scope="col" className={`${TH} num`}>
              Saved
            </th>
          </tr>
        </thead>
        <tbody>
          {view.rows.map(teamRow)}
          {rollup && (
            <tr className="border-b border-[var(--glass-border)]" data-testid="team-rollup">
              <RollupHeader label={rollupLabel(view)} />
              <td className={`px-4 py-3 text-right ${mono} text-[var(--text-secondary)]`}>
                {rollup.runs.toLocaleString("en-US")}
              </td>
              <td className={`px-4 py-3 text-right ${mono} font-semibold text-[var(--text-secondary)]`}>
                {formatUsd(rollup.sellMillicents)}
              </td>
              {/* Billed only: floored savings do not add across Teams. */}
              <ClaudeCells frontier={undefined} />
            </tr>
          )}
          {view.noTeam && teamRow(view.noTeam)}
        </tbody>
        <tfoot>
          <tr className="border-t border-[var(--ink)]">
            <th scope="row" className={`px-4 py-3 text-left font-semibold ${STICKY}`}>
              Agent total
            </th>
            <td className="px-4 py-3" aria-hidden="true" />
            <td className={`px-4 py-3 text-right ${mono} font-semibold`}>
              {formatUsd(report.totalSellMillicents)}
            </td>
            <ClaudeCells frontier={report.frontier} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ByAgent({
  range,
  workerUsage,
  error,
}: {
  range: UsageRange;
  workerUsage: YagniCodeWorkerUsageReport | null;
  error: string | null;
}) {
  if (error !== null) {
    return (
      <p className="text-xs text-[var(--text-tertiary)]">
        Agent spend could not be loaded. Figures above are unaffected.
      </p>
    );
  }
  if (workerUsage === null) {
    return <p className="text-xs text-[var(--text-tertiary)]">Loading Agent spend</p>;
  }
  if (workerUsage.workers.length === 0) {
    return (
      <div className="empty dashed tight">
        <p>No Agent spend in this range.</p>
      </div>
    );
  }
  return (
    <div className="tbl usage-tbl" data-testid="worker-usage">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[var(--glass-border)] text-left">
            <th scope="col" className={TH}>
              Agent
            </th>
            <th scope="col" className={`${TH} num`}>
              Dispatches
            </th>
            <th scope="col" className={`${TH} num`}>
              Billed
            </th>
            <th scope="col" className={`${TH} num`}>
              At Claude list
            </th>
            <th scope="col" className={`${TH} num`}>
              Saved
            </th>
          </tr>
        </thead>
        <tbody>
          {orderAgents(workerUsage.workers).flatMap((worker) => [
            <tr key={worker.agentId} className="border-b border-[var(--glass-border)]">
              <th scope="row" className="px-4 py-3 text-left font-medium">
                <Link to={agentDrillPath(worker.agentId, range)} className={LINK}>
                  {worker.displayName ?? worker.workerSlug ?? worker.agentId}
                </Link>
              </th>
              <td className={`px-4 py-3 text-right ${mono} text-[var(--text-secondary)]`}>
                {worker.totalDispatches.toLocaleString("en-US")}
              </td>
              <td className={`px-4 py-3 text-right ${mono} font-semibold`}>
                {formatUsd(worker.totalSellMillicents)}
              </td>
              <ClaudeCells frontier={worker.frontier} />
            </tr>,
            /* The spend split by the standing job that spent it (ADR-0044):
               "Proctor · infra review". Attributed through the run's line,
               so a dispatch that belongs to no line is simply absent rather
               than invented into one; the rows can therefore sum to less
               than the Agent's total, which is why they are indented under
               it rather than presented as its parts. */
            ...(worker.jobs ?? []).map((job) => (
              <tr
                key={`${worker.agentId}-${job.jobId}`}
                className="border-b border-[var(--glass-border)]"
                data-testid={`worker-job-${job.jobId}`}
              >
                <td className="px-4 py-2 pl-10">
                  <span className="text-[var(--text-secondary)]">{job.label}</span>
                  {job.deskName ? (
                    <span className="ml-2 text-[var(--text-tertiary)]">{job.deskName}</span>
                  ) : null}
                </td>
                <td className={`px-4 py-2 text-right ${mono} text-[var(--text-tertiary)]`}>
                  {job.runs.toLocaleString("en-US")} runs
                </td>
                <td className={`px-4 py-2 text-right ${mono} text-[var(--text-secondary)]`}>
                  {formatUsd(job.sellMillicents)}
                </td>
                <ClaudeCells frontier={job.frontier} pad="py-2" />
              </tr>
            )),
          ])}
        </tbody>
        <tfoot>
          <tr className="border-t border-[var(--ink)]">
            <th scope="row" className="px-4 py-3 text-left font-semibold">
              Agent total
            </th>
            <td className={`px-4 py-3 text-right ${mono} text-[var(--text-secondary)]`}>
              {workerUsage.totalDispatches.toLocaleString("en-US")}
            </td>
            <td className={`px-4 py-3 text-right ${mono} font-semibold`}>
              {formatUsd(workerUsage.totalSellMillicents)}
            </td>
            <ClaudeCells frontier={workerUsage.frontier} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
